import React, { useState, useMemo, useCallback, useEffect } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useGetFlags, useDeleteFlag } from "api/flags/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getTheme2SearchFilterFieldCss } from "utils/theme2MultiSelect";
import { useTranslation } from "react-i18next";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import classNames from "classnames";

const Flags = ({ activeColor = "secondary", account, embedded = false, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const perms = account?.permissions || {};

  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const isTheme2 = shellThemeVariant === "2";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const { data: flagsData, isLoading } = useGetFlags(undefined, {
    page,
    pageSize,
    search: debouncedSearch,
  });
  const deleteFlagMutation = useDeleteFlag();

  const handleDeleteClick = useCallback((id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteFlagMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const FlagsTable = isTheme2 ? ReactTable2 : ReactTable;

  const rows = useMemo(
    () =>
      (flagsData?.results || []).map((flag) => ({
        documentId: flag.documentId,
        name: flag.name,
        description: flag.description,
        module: flag.module,
        priority: flag.priority,
      })),
    [flagsData]
  );

  const columns = useMemo(
    () => [
      {
        Header: "#",
        id: "rowNum",
        width: 56,
        Cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
      },
      { Header: t("flags.name"), accessor: "name" },
      { Header: t("flags.description"), accessor: "description" },
      { Header: t("flags.module"), accessor: "module" },
      { Header: t("flags.priority"), accessor: "priority" },
      {
        Header: "",
        id: "actions",
        accessor: () => "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const documentId = row.original?.documentId;
          return (
            <div className="actions-right text-right d-flex justify-content-end gap-2">
              {perms.flags_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/flags/edit/${documentId}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {perms.flags_delete && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(documentId);
                  }}
                  disabled={deleteFlagMutation.isPending}
                >
                  {deleteFlagMutation.isPending && selectedDeleteId === documentId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [t, perms, navigate, deleteFlagMutation.isPending, selectedDeleteId, handleDeleteClick, page, pageSize]
  );

  return (
    <div
      className={
        embedded
          ? undefined
          : classNames("content", "search-flags", {
              "search-flags--theme2": isTheme2,
              "theme2-admin-content-compact": isTheme2,
              "theme2-flags-inset": isTheme2,
            })
      }
      style={embedded ? { width: "100%" } : undefined}
    >
      {!embedded && (
        <style>
          {`
          .search-flags-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .search-flags-card-header h2,
          .search-flags-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .search-flags .align-items-center {
            margin-top: 3px;
          }
          .search-flags .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          ${
            isTheme2
              ? getTheme2SearchFilterFieldCss({
                  pageScopeSelector: ".search-flags.search-flags--theme2",
                  filtersSelector: ".search-flags-filters",
                  rowClass: "search-flags-filters-row",
                  colClass: "search-flags-filter-col",
                })
              : ""
          }
        `}
        </style>
      )}
      {isTheme2 && (
        <style>
          {`
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
        </style>
      )}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className={embedded ? undefined : "search-flags-card-header"}>
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-0">{t("flags.title")}</h2>
                {perms.flags_add && (
                  <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: "white" }}
                    onClick={() => navigate("/flags/add")}
                  >
                    {t("flags.addFlag")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div className="search-flags-filters">
                <Row className={isTheme2 ? "search-flags-filters-row" : "mb-2"}>
                  <Col md="4" className={isTheme2 ? "search-flags-filter-col" : undefined}>
                    <FormGroup>
                      <Label>{t("flags.search")}</Label>
                      <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("flags.searchPlaceholder")}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
              <div
                className="inventory-table-wrap"
                style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
              >
                <FlagsTable
                  activeColor={activeColor}
                  data={rows}
                  columns={columns}
                  disableViewportClamp={embedded || !isTheme2}
                  className="-striped -highlight sticky-stock-table"
                  manualPagination
                  pageCount={flagsData?.pagination?.pageCount || 1}
                  pageIndex={page - 1}
                  totalItems={flagsData?.pagination?.total}
                  pageSize={pageSize}
                  loading={isLoading}
                  loadingText={t("flags.loading")}
                  noDataText={t("flags.noFlagsFound")}
                  onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  onRowClick={(row) => {
                    if (row?.documentId) navigate(`/flags/edit/${row.documentId}`);
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: "100px" }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("flags.deleteFlag")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("flags.deleteConfirmation")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteFlagMutation.isPending}
          >
            {t("flags.cancel")}
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white"
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteFlagMutation.isPending}
          >
            {deleteFlagMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                {t("flags.deleting")}
              </>
            ) : (
              t("flags.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Flags;
